"use client";

import { useEffect, useRef, useState } from "react";
import { toGlbProxyUrl } from "@/utils/productImageMedia";

const MODEL_VIEWER_SCRIPT =
  "https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js";

let modelViewerScriptPromise = null;

const loadModelViewerScript = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("SSR"));
  }
  if (customElements.get("model-viewer")) {
    return Promise.resolve();
  }
  if (!modelViewerScriptPromise) {
    modelViewerScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${MODEL_VIEWER_SCRIPT}"]`);
      if (existing) {
        customElements.whenDefined("model-viewer").then(resolve).catch(reject);
        return;
      }
      const script = document.createElement("script");
      script.type = "module";
      script.src = MODEL_VIEWER_SCRIPT;
      script.onload = () => {
        customElements.whenDefined("model-viewer").then(resolve).catch(reject);
      };
      script.onerror = () => reject(new Error("model-viewer script blocked or failed"));
      document.head.appendChild(script);
    });
  }
  return modelViewerScriptPromise;
};

export default function GlbModelViewer({
  modelUrl,
  height = 470,
  className = "",
  onPoster,
  interactive = true,
  lazy = false,
}) {
  const containerRef = useRef(null);
  const onPosterRef = useRef(onPoster);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    onPosterRef.current = onPoster;
  }, [onPoster]);

  useEffect(() => {
    if (!modelUrl) {
      return undefined;
    }

    let cancelled = false;
    let loadTimeoutId = null;
    const posterTimers = [];
    const container = containerRef.current;

    const mountViewer = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadModelViewerScript();
        if (cancelled || !containerRef.current) {
          return;
        }
        containerRef.current.innerHTML = "";
        const viewerEl = document.createElement("model-viewer");
        viewerEl.setAttribute("alt", "3D product model");
        viewerEl.setAttribute("crossorigin", "anonymous");
        if (interactive) {
          viewerEl.setAttribute("camera-controls", "");
        } else {
          viewerEl.setAttribute("interaction-prompt", "none");
          viewerEl.setAttribute("disable-tap", "");
        }
        if (lazy || !interactive) {
          viewerEl.setAttribute("loading", "lazy");
        }
        viewerEl.setAttribute("auto-rotate", "");
        viewerEl.setAttribute("auto-rotate-delay", "0");
        viewerEl.setAttribute("rotation-per-second", "60deg");
        viewerEl.setAttribute("shadow-intensity", "1");
        viewerEl.setAttribute("environment-image", "neutral");
        viewerEl.setAttribute("exposure", "1");
        viewerEl.style.width = "100%";
        viewerEl.style.height = `${height}px`;
        viewerEl.style.background = interactive ? "#f2f3f7" : "#ffffff";
        viewerEl.style.borderRadius = interactive ? "8px" : "0";

        const capturePoster = (attempt = 0) => {
          if (cancelled || !onPosterRef.current) {
            return;
          }
          try {
            const poster = viewerEl.toDataURL("image/jpeg", 0.85);
            if (poster && poster.startsWith("data:image") && poster.length > 1000) {
              onPosterRef.current(poster);
              return;
            }
          } catch (posterError) {
            // Retry below. A still frame is optional if the canvas is not ready yet.
          }
          if (attempt < 8) {
            const timerId = setTimeout(() => capturePoster(attempt + 1), 250);
            posterTimers.push(timerId);
          }
        };

        const onError = (event) => {
          if (cancelled || !viewerEl.isConnected || viewerEl.loaded) {
            return;
          }
          const sourceError = event?.detail?.sourceError;
          if (sourceError && String(sourceError).includes("AbortError")) {
            return;
          }
          if (containerRef.current) {
            containerRef.current.innerHTML = "";
          }
          setError("Unable to load 3D model.");
          setLoading(false);
        };

        const onLoad = () => {
          clearTimeout(loadTimeoutId);
          if (!cancelled) {
            setLoading(false);
            setError(null);
            capturePoster();
          }
        };

        viewerEl.addEventListener("load", onLoad);
        viewerEl.addEventListener("error", onError);
        containerRef.current.appendChild(viewerEl);
        viewerEl.setAttribute("src", toGlbProxyUrl(modelUrl));
        loadTimeoutId = setTimeout(() => {
          if (!cancelled && viewerEl.isConnected && !viewerEl.loaded) {
            onError();
          }
        }, lazy ? 120000 : 60000);
        if (viewerEl.loaded) {
          onLoad();
        }
      } catch (e) {
        if (!cancelled) {
          setError("3D viewer failed to load.");
          setLoading(false);
        }
      }
    };

    mountViewer();

    return () => {
      cancelled = true;
      if (loadTimeoutId) {
        clearTimeout(loadTimeoutId);
      }
      posterTimers.forEach((timerId) => clearTimeout(timerId));
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [modelUrl, height, interactive, lazy]);

  if (!modelUrl) {
    return null;
  }

  return (
    <div
      className={`GlbModelViewer ${className}`}
      style={{
        position: "relative",
        width: "100%",
        height,
        pointerEvents: interactive ? "auto" : "none",
      }}
    >
      {loading && !error && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.75)",
            zIndex: 1,
          }}
        >
          Loading 3D…
        </div>
      )}
      {error &&
        (interactive ? (
          <div style={{ padding: 16, color: "#cf1322", minHeight: height }}>{error}</div>
        ) : (
          <div
            style={{
              height,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#8c8c8c",
              background: "#f7f7f7",
            }}
          >
            3D
          </div>
        ))}
      <div ref={containerRef} />
    </div>
  );
}
